import {
  CompanyCustomBenefitAssignmentEmployee,
  CustomBenefitEmployee,
  MonthField,
  PendingCustomBenefitEmployee
} from '@pxtra-client-lib-legacy/gen';
import Util from '../../../../misc/util';
import type FutureAssignment from '../../../../pages/benefits/customBenefitDialogs/interfaces/futureAssignment';

export function getAssignmentType(employee: CompanyCustomBenefitAssignmentEmployee): string {
  const pending = employee.pending_assignments;
  const live = employee.live_assignments;
  if (pending.length > 0) {
    if (pending[0].reset_to_company_amount) return 'global';
    return pending[0].assignment_type ?? '';
  }
  return live[0]?.assignment_type ?? '';
}

export function getAssignmentAmount(employee: CompanyCustomBenefitAssignmentEmployee): number {
  const pending = employee.pending_assignments;
  const live = employee.live_assignments;
  if (pending.length > 0) {
    return pending[0].amount ?? 0;
  }
  return live[0]?.amount ?? 0;
}

export function getAssignmentId(employee: CompanyCustomBenefitAssignmentEmployee): string {
  const pending = employee.pending_assignments;
  if (pending.length > 0) {
    return pending[0].id ?? '';
  }
  return '';
}

export function getEditingStatus(
  employee: CompanyCustomBenefitAssignmentEmployee
): 'invalid' | 'changed' | 'unchanged' {
  const pending = employee.pending_assignments;
  if (pending.length > 0) {
    return (pending[0].assignment_type ?? '').includes('invalid') ? 'invalid' : 'changed';
  }
  return 'unchanged';
}

export function getMaximumValidAmount(
  employee: CompanyCustomBenefitAssignmentEmployee
): number | undefined {
  if (employee.maximum_valid_amount === null || employee.maximum_valid_amount === undefined) {
    return undefined;
  }
  return Number(employee.maximum_valid_amount);
}

export function getFutureAssignments(
  employee: CompanyCustomBenefitAssignmentEmployee
): Map<number, FutureAssignment> {
  const now = new Date();

  const getAccountingPeriodKey = (period: MonthField | null | undefined): string => {
    return `${period?.year ?? 0}-${period?.month ?? 0}`;
  };

  const pendingAssignments = employee.pending_assignments.filter(
    (assignment: PendingCustomBenefitEmployee) => {
      if (assignment.start_accounting_period == null) return false;
      const startDate = Util.getDate(assignment.start_accounting_period, 'start');
      return startDate > now;
    }
  );

  const pendingStartPeriods = new Set(
    pendingAssignments.map((assignment: PendingCustomBenefitEmployee) =>
      getAccountingPeriodKey(assignment.start_accounting_period)
    )
  );

  const liveAssignments = employee.live_assignments.filter((assignment: CustomBenefitEmployee) => {
    if (assignment.start_accounting_period == null) return false;
    const startDate = Util.getDate(assignment.start_accounting_period, 'start');
    if (startDate <= now) return false;
    return !pendingStartPeriods.has(getAccountingPeriodKey(assignment.start_accounting_period));
  });

  const allFutureAssignments = [...pendingAssignments, ...liveAssignments]
    .map((assignment) => ({
      yearMonth: Util.getDate(assignment.start_accounting_period!, 'start'),
      value: assignment.amount ?? 0
    }))
    .sort((a, b) => a.yearMonth.getTime() - b.yearMonth.getTime());

  return new Map(allFutureAssignments.map((assignment, index) => [index, assignment]));
}
